/**
 * テーマの切り替え(D-132)。<html data-theme="…"> を付け替えるだけで、色・余白は CSS(src/styles/themes.css)が決める。
 *
 *  - 正式なテーマ(officialTheme): 外観設定の「適用する」で保存したもの。ユーザーごと・ブラウザごと(themes.ts)
 *  - プレビュー(previewTheme): デザイン比較の画面で一時的に見るもの。**保存しない**。null に戻すと正式なテーマに戻る
 *  - ログアウト・別の人のログインでは、その人のテーマ(無ければ現行)に切り替える
 * 業務のデータ・Firebase には一切触れない。
 */
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import {
  DEFAULT_THEME,
  clearBootTheme,
  normalizeTheme,
  readBootTheme,
  resolveOfficialTheme,
  userThemeKey,
  writeBootTheme,
  writeUserTheme,
  type ThemeId,
  type ThemeStorage,
} from './themes';

type ThemeContextValue = {
  /** いま画面に出ているテーマ(プレビュー中はプレビューのもの) */
  theme: ThemeId;
  /** 正式に使っているテーマ */
  officialTheme: ThemeId;
  /** プレビュー中のテーマ。null = プレビューしていない */
  previewTheme: ThemeId | null;
  /** 正式なテーマとして保存する(ログインしていないときは何もしない)。保存できたら true */
  applyTheme: (theme: ThemeId) => boolean;
  /** 一時的に見るテーマを決める。null で元に戻す(保存しない) */
  setPreviewTheme: (theme: ThemeId | null) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** localStorage が使えない環境では何もしない入れ物にする(画面は現行で動く) */
function storageOf(): ThemeStorage {
  try {
    return window.localStorage;
  } catch {
    return { getItem: () => null, setItem: () => undefined, removeItem: () => undefined };
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const uid = status === 'ready' ? (user?.id ?? null) : null;
  const [officialTheme, setOfficialTheme] = useState<ThemeId>(() => readBootTheme(storageOf())?.theme ?? DEFAULT_THEME);
  const [previewTheme, setPreviewTheme] = useState<ThemeId | null>(null);

  // ログインの状態が変わったら、その人のテーマに切り替える。ログアウトしたら開いた瞬間用の記録も消す(共有PC対策)
  useEffect(() => {
    const storage = storageOf();
    const next = resolveOfficialTheme({ status, uid, storage });
    setOfficialTheme(next);
    if (status === 'loading' || status === 'error') return;
    if (uid) writeBootTheme(storage, uid, next);
    else clearBootTheme(storage);
    // ログインしている人が変わったら、前の人のプレビューも残さない
    setPreviewTheme(null);
  }, [status, uid]);

  // 同じ人が別のタブでテーマを変えたら、このタブにも反映する
  useEffect(() => {
    if (!uid) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === userThemeKey(uid)) setOfficialTheme(normalizeTheme(e.newValue));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [uid]);

  const theme = previewTheme ?? officialTheme;

  // 描画の前に付け替える(ちらつかせない)。ネイティブの入力欄・スクロールバーの明暗は CSS の color-scheme で合わせる
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const applyTheme = useCallback(
    (next: ThemeId) => {
      if (!uid) return false;
      const t = normalizeTheme(next);
      const storage = storageOf();
      writeUserTheme(storage, uid, t);
      writeBootTheme(storage, uid, t);
      setOfficialTheme(t);
      return true;
    },
    [uid],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, officialTheme, previewTheme, applyTheme, setPreviewTheme }),
    [theme, officialTheme, previewTheme, applyTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme は ThemeProvider の内側で使ってください');
  return ctx;
}
